import React, { useMemo, useState } from "react";
import MichelleTrentImg from "../candidates/MichelleTrent.jpg";
import KimberlyPImg    from "../candidates/KIMBERLYP.jpg";
import JonathanMImg    from "../candidates/jonathanme.jpg";
import LouisKImg       from "../candidates/Louisk.jpg";
import DawnBImg        from "../candidates/DAWNB.jpg";

const HEADSHOTS = {
  Michelle: MichelleTrentImg,
  Kim:      KimberlyPImg,
  Jonathan: JonathanMImg,
  Louis:    LouisKImg,
  Dawn:     DawnBImg,
};

const PROFILE_LINKS = {
  Michelle: "https://jobs.aiarchitech.com/client/candidates/michellet",
  Kim:      "https://jobs.aiarchitech.com/client/candidates/kimberlyp",
  Jonathan: "https://jobs.aiarchitech.com/client/candidates/jonathanm",
  Louis:    "https://jobs.aiarchitech.com/client/candidates/louisk",
  Dawn:     "https://jobs.aiarchitech.com/client/candidates/dawnb",
};

const INTERVIEW_LINKS = {
  Michelle: "https://fathom.video/share/acE3DJZUtoyFzk4S4G9FD56bvwjw79NR",
  Kim:      "https://fathom.video/share/ve9zdMqBaZzujirKYPzanA8Ft8aLy4rm",
  Jonathan: "https://fathom.video/share/heWzUnX_pPMANowu3ey32AVGXWT5j2vy",
  Louis:    "https://fathom.video/share/qsv8A998haHy3za8BpXRiqFVw1q2xix2",
  Dawn:     "https://fathom.video/share/Z8GSNvbPBD9nWF-gAF9_65Rin3eFuz8F",
};

const rankedOverview = [
  {
    rank: 1,
    name: "Michelle",
    title: "Senior Director of Value-Based Care and Healthcare Partnerships",
    strength: "National physician partnership leadership across 200+ relationships and 800+ clinics",
    summary:
      "15 plus years building and leading physician and clinic partnerships at DaVita, Fresenius, CVS Health, and Aledade. Her best story: when a hospital CEO wouldn't sell, she structured a new non-competing clinic with a right of first refusal on the others and grew the relationship across the region. Deep in CMS, regulated healthcare, and executive-level provider relationships. Compensation target is at the top of the range and she framed the move as lateral, so drive and pay alignment are worth a direct conversation.",
  },
  {
    rank: 2,
    name: "Kim",
    title: "Chief Operating Officer and Customer Success Executive in Healthcare and Telehealth",
    strength: "Lifted provider adoption from 36 to 91 percent across 4,500 prescribers in 11 months",
    summary:
      "Built referral engines and growth systems inside healthcare and telehealth, including a channel that became one of her company's top revenue drivers and a $12M expansion pipeline. She thinks in repeatable systems, builds trust through a structured listen-first approach, and already speaks fluently about the fertility and women's health prescribing opportunity. Grew up around pharmacy and worked as a licensed provider, so compliance is second nature. Currently running fractional engagements, so confirming full-time commitment is the step before advancing.",
  },
  {
    rank: 3,
    name: "Jonathan",
    title: "Vice President of Business Development and Strategic Partnerships",
    strength: "Built two BD departments from scratch and drove $240M+ in new revenue",
    summary:
      "Built two entire BD departments from nothing, led a 30-person team to above quota for 12 straight quarters, and closed a $37M sovereign deal. Sharp commercial instincts, disciplined pipeline governance, and real hunger. The honest tradeoff is domain: his background is financial services and brand partnerships, not clinical referrals, so the prescriber relationship and compliance side would be new ground. The right bet if you want the highest revenue ceiling and are willing to support that ramp.",
  },
  {
    rank: 4,
    name: "Louis",
    title: "Sales and Customer Success Director with Healthcare Provider Growth Experience",
    strength: "Grew new patient enrollments from 15 to 500 per month as a company's first sales hire",
    summary:
      "Rose from cold-calling SDR at ZocDoc to enterprise accounts to building a sales division from scratch. Reactivated a dormant national optometry brand and expanded it from around 100 to 2,000+ locations. Still picks up the phone daily alongside his reps. Has worked with OBGYN and a wide range of other providers throughout his career. Compensation is modest and flexible, and he can start within one week.",
  },
  {
    rank: 5,
    name: "Dawn",
    title: "Senior Director of Strategic Partnerships and Founder with 30 Years in Healthcare Sales",
    strength: "Generated $94M+ in revenue over five years through C-suite executive relationships",
    summary:
      "30 plus years opening and keeping C-suite relationships in healthcare, including OBGYN groups and major health systems, with a disciplined multi-touch follow-up system that rarely lets a relationship go cold. Also founded and ran her own national organization across 48 states. Her wins are in healthcare staffing rather than prescription referrals, and she prefers large accounts over a high volume of smaller clinics, so both points are worth aligning early. Compensation requires a guaranteed structure and should be settled before advancing.",
  },
];

const dimensions = [
  { id: 1, label: "Healthcare Partnership Development" },
  { id: 2, label: "Building From Zero and Pipeline Construction" },
  { id: 3, label: "Revenue and Commercial Thinking" },
  { id: 4, label: "Partner Conversion and Lifecycle Management" },
  { id: 5, label: "SDR Team Leadership" },
  { id: 6, label: "Regulated Healthcare and Compliance" },
  { id: 7, label: "Fertility and Women's Health Exposure" },
  { id: 8, label: "Ambition and Drive" },
];

const scores = {
  Michelle: [9, 8, 8, 8, 8, 9, 6, 7],
  Kim:      [9, 8, 8, 9, 7, 9, 7, 8],
  Jonathan: [9, 9, 9, 8, 9, 6, 4, 9],
  Louis:    [8, 8, 7, 8, 9, 8, 6, 8],
  Dawn:     [8, 8, 8, 8, 8, 9, 7, 9],
};

const comparisons = [
  {
    sectionTitle: "Michelle vs. the Field",
    items: [
      {
        heading: "Michelle vs. Kim",
        body: "Both lead with trust and relationships, but built differently. Michelle brings physician partnership leadership at national scale, with acquisitions and joint ventures behind her. Kim brings stronger systems architecture and deeper familiarity with the fertility and women's health market specifically. Physician relationship depth favors Michelle; repeatable system building favors Kim.",
      },
      {
        heading: "Michelle vs. Jonathan",
        body: "Michelle has spent her career in the exact motion this role needs: winning over clinical decision makers and turning those relationships into lasting partnerships. Jonathan brings bigger revenue numbers and a more aggressive build-from-zero track record, but outside healthcare. The tradeoff is healthcare depth versus raw commercial ceiling.",
      },
      {
        heading: "Michelle vs. Louis",
        body: "Michelle operates at a larger scale with deeper provider and acquisition experience. Louis brings more hunger, lower cost, and a more recent proof point of building a sales function from nothing. Credibility in front of senior clinic executives favors Michelle; scrappy, hands-on execution from day one favors Louis.",
      },
      {
        heading: "Michelle vs. Dawn",
        body: "Both are relationship-first, executive-access operators with deep healthcare roots. Michelle's edge is specific physician and clinic partnership experience plus a stronger team leadership record. Dawn's edge is raw intensity and relentless follow-up discipline built over 30 years. Role fit favors Michelle; energy and C-suite hunger favor Dawn.",
      },
    ],
  },
  {
    sectionTitle: "Kim vs. the Field",
    items: [
      {
        heading: "Kim vs. Michelle",
        body: "Kim brings stronger systems thinking and earlier familiarity with the fertility and women's health prescribing opportunity. Michelle brings direct physician partnership leadership at larger scale with acquisition and joint venture experience. Building a scalable system favors Kim; pure clinical relationship depth favors Michelle.",
      },
      {
        heading: "Kim vs. Jonathan",
        body: "Kim builds inside healthcare with deep compliance knowledge and a trust-first approach that fits clinical buyers. Jonathan brings enterprise deal-making and pipeline infrastructure, all outside healthcare. Domain alignment favors Kim; raw commercial scale and deal size favor Jonathan.",
      },
      {
        heading: "Kim vs. Louis",
        body: "Kim designs the system and manages the full partnership lifecycle. Louis builds sales teams and grows accounts through hands-on outreach, with the strongest SDR leadership in this group. Partnership system design and lifecycle management favor Kim; outbound team execution and provider account growth favor Louis.",
      },
      {
        heading: "Kim vs. Dawn",
        body: "Kim builds trust through a structured, multi-touch arc and thinks in compounding systems. Dawn goes straight to the decision maker and follows up relentlessly with a personal, old-school touch. Strategic, systems-first operating favors Kim; raw executive access and cold-start relationship building favor Dawn.",
      },
    ],
  },
  {
    sectionTitle: "Jonathan vs. the Field",
    items: [
      {
        heading: "Jonathan vs. Michelle",
        body: "Jonathan's edge is commercial scale: $240M in revenue, a 30-person team above quota for 12 consecutive quarters, and a $37M sovereign deal. Michelle's edge is spending her entire career in healthcare, building physician and clinic partnerships the way this role specifically needs. Higher ceiling favors Jonathan; shorter ramp favors Michelle.",
      },
      {
        heading: "Jonathan vs. Kim",
        body: "Jonathan is the stronger pure revenue and deal-making operator, with disciplined pipeline governance and CAC tracking. Kim is the stronger healthcare-native builder with compliance fluency and a track record of turning provider relationships into repeatable growth. Commercial infrastructure favors Jonathan; healthcare-specific systems and compliance depth favor Kim.",
      },
      {
        heading: "Jonathan vs. Louis",
        body: "Both have built sales functions from nothing, but at different altitudes. Jonathan built a 30-person department and drove $240M. Louis built a lean team and grew enrollments more than 30-fold inside healthcare. Strategic leadership and enterprise deal making favor Jonathan; provider relationship trust and day-to-day team coaching favor Louis.",
      },
      {
        heading: "Jonathan vs. Dawn",
        body: "Jonathan brings structured pipeline infrastructure, data-driven partner evaluation, and large-team leadership with disciplined KPIs. Dawn brings decades of healthcare executive relationships and a founder's ownership mindset. Scalable, metrics-driven BD function favors Jonathan; winning clinical executives through warmth and persistence favors Dawn.",
      },
    ],
  },
  {
    sectionTitle: "Louis vs. the Field",
    items: [
      {
        heading: "Louis vs. Michelle",
        body: "Louis brings more hunger, a lower cost, and a recent proof of building a sales engine from scratch as a first hire. Michelle brings a larger track record, bigger team leadership experience, and more credibility in front of senior healthcare executives. Scrappy, hands-on building from day one favors Louis; operating at the scale this role is heading toward favors Michelle.",
      },
      {
        heading: "Louis vs. Kim",
        body: "Louis is stronger on outbound execution and SDR leadership, with his team-building experience directly in healthcare. Kim is stronger on systems design and the full partnership lifecycle. Getting the outbound engine running fast favors Louis; building the partnership program that compounds over time favors Kim.",
      },
      {
        heading: "Louis vs. Jonathan",
        body: "Louis has spent his entire career inside healthcare, building provider relationships through listening and trust. Jonathan has closed large multi-year deals in financial services and brand partnerships. Healthcare provider alignment favors Louis; raw commercial scale and enterprise deal structure favor Jonathan.",
      },
      {
        heading: "Louis vs. Dawn",
        body: "Both are hands-on, relationship-led operators. Louis's edge is SDR team building, build-from-zero experience, and flexible compensation. Dawn's edge is C-suite access, 30 years of healthcare relationships, and the gravitas of someone who built and ran a national organization. Team execution favors Louis; executive relationship capital favors Dawn.",
      },
    ],
  },
  {
    sectionTitle: "Dawn vs. the Field",
    items: [
      {
        heading: "Dawn vs. Michelle",
        body: "Dawn brings exceptional intensity, C-suite access, and a founder's ownership mindset. Michelle brings a track record built entirely in physician and clinic partnerships, which is the specific motion this role needs, plus stronger team leadership depth. Energy and executive gravitas favor Dawn; direct role fit and coaching experience favor Michelle.",
      },
      {
        heading: "Dawn vs. Kim",
        body: "Dawn is the stronger pure hunter: straight to the decision maker, fast to close, and persistent for years. Kim is the stronger systems operator, building structures that make partnerships grow and compound. Winning new clinic relationships through executive access favors Dawn; building the repeatable revenue engine favors Kim.",
      },
      {
        heading: "Dawn vs. Jonathan",
        body: "Dawn's healthcare depth and executive relationship capital give her an edge in the clinical partnership motion this role requires. Jonathan's edge is structured pipeline infrastructure and scalable team leadership with disciplined KPIs. Winning clinical executive trust favors Dawn; building a metrics-driven BD function that scales to $100M favors Jonathan.",
      },
      {
        heading: "Dawn vs. Louis",
        body: "Dawn brings more healthcare experience, executive gravitas, and a stronger ownership track record. Louis brings more recent build-from-zero experience, stronger SDR methodology, and a more flexible compensation structure. Team building and SDR execution favor Louis; executive relationship capital and C-suite credibility favor Dawn.",
      },
    ],
  },
];

function clamp(n, min, max) { return Math.max(min, Math.min(max, n)); }
function tone(score) {
  if (score >= 8) return "good";
  if (score >= 6) return "mid";
  return "warn";
}

function Avatar({ name, size = "big" }) {
  const dim = size === "big" ? 88 : 28;
  return (
    <img
      src={HEADSHOTS[name]}
      alt={name}
      loading="lazy"
      style={{
        width: dim,
        height: dim,
        borderRadius: 999,
        objectFit: "cover",
        objectPosition: "top",
        flexShrink: 0,
        border: size === "big" ? "3px solid rgba(54,209,255,.85)" : "2px solid rgba(54,209,255,.6)",
        background: "rgba(255,255,255,.06)",
        boxShadow: size === "big" ? "0 16px 40px rgba(0,0,0,.35), 0 0 0 10px rgba(54,209,255,.10)" : "none",
      }}
    />
  );
}

function NewTabLink({ href, className, children }) {
  return (
    <a className={className} href={href} target="_blank" rel="noreferrer noopener">
      {children}
    </a>
  );
}

function SectionShell({ kicker, title, subtitle, right, children }) {
  return (
    <section className="block">
      <div className="blockTop">
        <div className="blockTopLeft">
          {kicker && <div className="blockKicker">{kicker}</div>}
          <div className="blockTitleRow">
            <h2 className="blockTitle">{title}</h2>
            {right ? <div className="blockRight">{right}</div> : null}
          </div>
          {subtitle ? <div className="blockSubtitle">{subtitle}</div> : null}
        </div>
      </div>
      <div className="blockBody">{children}</div>
    </section>
  );
}

export default function CandidateFitDashboardHeadOfPartnerships() {
  const [openSections, setOpenSections] = useState({ "Michelle vs. the Field": true });
  const candidateKeys = useMemo(() => ["Michelle", "Kim", "Jonathan", "Louis", "Dawn"], []);
  const toggleSection = (title) =>
    setOpenSections((p) => ({ ...p, [title]: !p[title] }));

  return (
    <div className="page">
      <div className="bgGlow" aria-hidden="true" />
      <div className="container">

        <header className="hero">
          <div className="heroKicker">Voshell's Pharmacy</div>
          <h1 className="heroTitle">Head of Partnerships — Candidate Fit Dashboard</h1>
          <p className="heroSub">
            Ranked overview, dimension scoring, and interview-based fit notes for five finalists.
          </p>
        </header>

        {/* SECTION 1 — Ranked Overview */}
        <SectionShell
          kicker="Section 01"
          title="Comparative Fit Summary"
          subtitle="A high-level snapshot of each candidate, ranked from strongest overall fit to least immediate fit for the Head of Partnerships role."
        >
          <div className="grid3top">
            {rankedOverview.slice(0, 3).map((c) => (
              <article key={c.name} className={`card rankCard ${c.rank === 1 ? "cardHero" : ""}`}>
                <div className="rankRow">
                  <div className="rankBadge">#{c.rank}</div>
                  <div className="rankName">{c.name}</div>
                </div>
                <div className="rankAvatarWrap">
                  <Avatar name={c.name} size="big" />
                </div>
                <div className="rankTitle">{c.title}</div>
                <div className="rankStrength">{c.strength}</div>
                <p className="rankBody">{c.summary}</p>
                <div className="cardFooter">
                  <NewTabLink className="selectBtn" href={PROFILE_LINKS[c.name]}>
                    Open full profile →
                  </NewTabLink>
                  <NewTabLink className="interviewBtn" href={INTERVIEW_LINKS[c.name]}>
                    Watch interview →
                  </NewTabLink>
                </div>
              </article>
            ))}
          </div>
          <div className="grid2bottom">
            {rankedOverview.slice(3).map((c) => (
              <article key={c.name} className="card rankCard">
                <div className="rankRow">
                  <div className="rankBadge">#{c.rank}</div>
                  <div className="rankName">{c.name}</div>
                </div>
                <div className="rankAvatarWrap">
                  <Avatar name={c.name} size="big" />
                </div>
                <div className="rankTitle">{c.title}</div>
                <div className="rankStrength">{c.strength}</div>
                <p className="rankBody">{c.summary}</p>
                <div className="cardFooter">
                  <NewTabLink className="selectBtn" href={PROFILE_LINKS[c.name]}>
                    Open full profile →
                  </NewTabLink>
                  <NewTabLink className="interviewBtn" href={INTERVIEW_LINKS[c.name]}>
                    Watch interview →
                  </NewTabLink>
                </div>
              </article>
            ))}
          </div>
        </SectionShell>

        {/* SECTION 2 — Score Table */}
        <SectionShell
          kicker="Section 02"
          title="Score Comparison"
          subtitle="Dimension-by-dimension scores (1–10) based on CV evidence and interview performance."
        >
          <div className="card tableCard">
            <div className="tableWrap" role="region" aria-label="Score table">
              <table className="table">
                <thead>
                  <tr>
                    <th className="th dimCol">Dimension</th>
                    {candidateKeys.map((k) => (
                      <th key={k} className="th candCol">
                        <div className="thCand">
                          <Avatar name={k} size="small" />
                          <div className="thName">{k}</div>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {dimensions.map((d, idx) => (
                    <tr key={d.id} className="tr">
                      <td className="td dimCell">
                        <div className="dimLabel">{d.id}. {d.label}</div>
                      </td>
                      {candidateKeys.map((k) => {
                        const s = scores[k][idx];
                        const pct = clamp((s / 10) * 100, 0, 100);
                        const t = tone(s);
                        return (
                          <td key={`${d.id}-${k}`} className="td scoreCell">
                            <div className="scoreBox">
                              <div className="scoreTop">
                                <div className="scoreNum">{s}</div>
                                <div className={`scoreDot ${t}`} aria-hidden="true" />
                              </div>
                              <div className="bar">
                                <div className={`barFill ${t}`} style={{ width: `${pct}%` }} />
                              </div>
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </SectionShell>

        {/* SECTION 3 — Comparisons */}
        <SectionShell
          kicker="Section 03"
          title="Candidate Comparisons"
          subtitle="Interview-based notes comparing each candidate against the others across the dimensions that matter most for this role."
          right={<span className="tinyNote">Expandable sections</span>}
        >
          <div className="card compareShell">
            <div className="compareShellInner">
              {comparisons.map((sec, i) => {
                const isOpen = !!openSections[sec.sectionTitle];
                return (
                  <div key={sec.sectionTitle} className={`rowAcc ${i === 0 ? "first" : ""}`}>
                    <button
                      type="button"
                      className="rowAccHead"
                      onClick={() => toggleSection(sec.sectionTitle)}
                      aria-expanded={isOpen}
                    >
                      <div className="rowAccLeft">
                        <div className="rowAccTitle">{sec.sectionTitle}</div>
                        <div className="rowAccHint">Click to expand</div>
                      </div>
                      <div className="rowAccIcon" aria-hidden="true">{isOpen ? "–" : "+"}</div>
                    </button>
                    {isOpen && (
                      <div className="rowAccBody">
                        {sec.items.map((it) => (
                          <div key={it.heading} className="compareRow">
                            <div className="compareHeading">{it.heading}</div>
                            <div className="compareText">{it.body}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </SectionShell>

      </div>

      <style>{`
        :root {
          --aa-navy: #01014C;
          --aa-navy2: #0A0A80;
          --aa-cyan: #36D1FF;
          --aa-cream: #FFFDFB;
          --aa-orange: #EB5D2A;
        }

        html, body { background: var(--aa-navy); }

        .page {
          min-height: 100vh;
          padding: 26px 18px 60px;
          position: relative;
          overflow: hidden;
          color: #eaf1ff;
          font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial;
          font-size: 16px;
        }

        .bgGlow {
          position: absolute;
          inset: -220px;
          background:
            radial-gradient(900px 620px at 18% 18%, rgba(54,209,255,0.22), transparent 62%),
            radial-gradient(760px 520px at 78% 22%, rgba(10,10,128,0.22), transparent 58%),
            radial-gradient(980px 780px at 52% 92%, rgba(54,209,255,0.12), transparent 62%),
            radial-gradient(900px 700px at 50% 50%, rgba(255,253,251,0.04), transparent 65%);
          pointer-events: none;
          z-index: 0;
        }

        .container { max-width: 1320px; margin: 0 auto; position: relative; z-index: 1; }

        .hero { margin-bottom: 18px; }
        .heroKicker {
          font-size: 13px; letter-spacing: .14em; text-transform: uppercase;
          color: rgba(234,241,255,.78); margin-bottom: 10px;
        }
        .heroTitle {
          margin: 0; font-size: 34px; font-weight: 900; line-height: 1.06;
          background: linear-gradient(90deg, var(--aa-cream) 0%, rgba(54,209,255,.95) 55%, rgba(255,253,251,.85) 100%);
          -webkit-background-clip: text; background-clip: text; color: transparent;
        }
        .heroSub {
          margin: 12px 0 0; max-width: 72ch;
          color: rgba(234,241,255,.78); font-size: 15px; line-height: 1.65;
        }

        .block {
          margin-top: 14px;
          border-radius: 26px;
          border: 1px solid rgba(54,209,255,.22);
          background: rgba(255,255,255,.055);
          box-shadow: 0 22px 70px rgba(0,0,0,.34);
          backdrop-filter: blur(12px);
          overflow: hidden;
        }

        .blockTop {
          padding: 16px 16px 14px;
          background:
            radial-gradient(900px 200px at 10% 0%, rgba(54,209,255,.18), transparent 55%),
            radial-gradient(700px 220px at 90% 0%, rgba(10,10,128,.16), transparent 55%),
            rgba(0,0,0,.08);
          border-bottom: 1px solid rgba(54,209,255,.16);
        }

        .blockKicker {
          font-size: 13px; letter-spacing: .12em; text-transform: uppercase;
          color: rgba(234,241,255,.70); margin-bottom: 8px;
        }

        .blockTitleRow {
          display: flex; align-items: flex-end; justify-content: space-between;
          gap: 12px; flex-wrap: wrap;
        }

        .blockTitle {
          margin: 0; font-size: 20px; font-weight: 950;
          letter-spacing: .01em; color: rgba(245,248,255,.96);
        }

        .blockSubtitle {
          margin-top: 10px; color: rgba(234,241,255,.76);
          font-size: 14px; line-height: 1.7; max-width: 95ch;
        }

        .blockBody { padding: 14px 14px 16px; }
        .tinyNote { font-size: 13px; color: rgba(234,241,255,.70); }

        .card {
          border-radius: 22px;
          background: rgba(255,255,255,.065);
          border: 1px solid rgba(54,209,255,.18);
          box-shadow: 0 18px 60px rgba(0,0,0,.26);
          backdrop-filter: blur(12px);
        }

        .selectBtn {
          width: 100%; font-weight: 900; border-radius: 16px;
          background: var(--aa-cyan); color: var(--aa-navy);
          border: 1px solid rgba(54,209,255,.55);
          box-shadow: 0 0 22px rgba(54,209,255,.35);
          padding: 11px 14px; font-size: 14px;
          text-decoration: none; display: inline-flex;
          align-items: center; justify-content: center;
          cursor: pointer;
          transition: transform .12s ease, background .12s ease, box-shadow .12s ease;
        }
        .selectBtn:hover {
          background: #5FE2FF; box-shadow: 0 0 30px rgba(54,209,255,.55);
          transform: translateY(-1px);
        }

        .interviewBtn {
          width: 100%; font-weight: 900; border-radius: 16px;
          background: transparent; color: var(--aa-cyan);
          border: 1px solid rgba(54,209,255,.45);
          padding: 10px 14px; font-size: 14px;
          text-decoration: none; display: inline-flex;
          align-items: center; justify-content: center;
          cursor: pointer; margin-top: 8px;
          transition: transform .12s ease, background .12s ease, border-color .12s ease;
        }
        .interviewBtn:hover {
          background: rgba(54,209,255,.10); border-color: rgba(54,209,255,.8);
          transform: translateY(-1px);
        }

        /* 3-col top row */
        .grid3top {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 10px;
          margin-bottom: 10px;
        }
        @media (max-width: 720px) { .grid3top { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
        @media (max-width: 480px) { .grid3top { grid-template-columns: 1fr; } }

        /* 2-col bottom row, centered */
        .grid2bottom {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 10px;
          max-width: 66.8%;
          margin: 0 auto;
        }
        @media (max-width: 720px) { .grid2bottom { max-width: 100%; grid-template-columns: 1fr; } }

        .rankCard {
          padding: 16px; display: flex; flex-direction: column;
          min-height: 340px; position: relative; overflow: hidden;
        }
        .cardHero { border-color: rgba(54,209,255,.42); }
        .cardHero:before {
          content: ""; position: absolute; inset: 0;
          background: radial-gradient(520px 320px at 18% 0%, rgba(54,209,255,.18), transparent 58%);
          pointer-events: none;
        }

        .rankRow { display: flex; align-items: center; gap: 10px; position: relative; z-index: 1; }
        .rankBadge {
          width: 36px; height: 36px; display: grid; place-items: center;
          border-radius: 12px; background: rgba(0,0,0,.16);
          border: 1px solid rgba(54,209,255,.22);
          font-weight: 900; font-size: 14px;
        }
        .rankName { font-weight: 950; font-size: 17px; }

        .rankAvatarWrap {
          display: grid; place-items: center;
          margin: 14px 0 8px; position: relative; z-index: 1;
        }

        .rankTitle {
          margin-top: 10px; font-size: 12px; font-weight: 700;
          color: rgba(234,241,255,.60); position: relative; z-index: 1;
          text-transform: uppercase; letter-spacing: .06em; line-height: 1.5;
        }

        .rankStrength {
          margin-top: 6px; font-size: 13px; font-weight: 950;
          color: rgba(245,248,255,.96); position: relative; z-index: 1;
          line-height: 1.4;
        }

        .rankBody {
          margin: 8px 0 0; font-size: 12.5px; line-height: 1.7;
          color: rgba(234,241,255,.78); position: relative; z-index: 1;
          flex: 1;
        }
        .cardFooter { margin-top: auto; padding-top: 14px; position: relative; z-index: 1; }

        /* TABLE */
        .tableCard { padding: 14px; }
        .tableWrap {
          overflow: auto; border-radius: 16px;
          border: 1px solid rgba(54,209,255,.14);
          background: rgba(0,0,0,.10);
        }
        table { width: 100%; border-collapse: separate; border-spacing: 0; min-width: 860px; }
        .th {
          position: sticky; top: 0; z-index: 2;
          background: rgba(1,1,76,.92);
          border-bottom: 1px solid rgba(54,209,255,.16);
          text-align: left; padding: 12px;
          font-size: 12px; letter-spacing: .08em;
          text-transform: uppercase; color: rgba(234,241,255,.72);
        }
        .dimCol { width: 280px; }
        .candCol { width: 130px; }
        .td { padding: 12px; border-bottom: 1px solid rgba(54,209,255,.10); vertical-align: top; }
        .dimLabel { font-size: 14px; color: rgba(245,248,255,.88); line-height: 1.6; }

        .thCand { display: flex; align-items: center; gap: 8px; }
        .thName { font-weight: 950; letter-spacing: .01em; color: rgba(245,248,255,.92); font-size: 13px; }

        .scoreBox {
          border-radius: 14px; padding: 10px;
          border: 1px solid rgba(54,209,255,.12);
          background: rgba(255,255,255,.05);
        }
        .scoreTop { display: flex; align-items: center; justify-content: space-between; gap: 10px; margin-bottom: 8px; }
        .scoreNum { font-size: 20px; font-weight: 950; }
        .scoreDot {
          width: 10px; height: 10px; border-radius: 999px;
          border: 1px solid rgba(255,255,255,.26);
        }
        .scoreDot.good { background: rgba(60,245,128,0.7); }
        .scoreDot.mid  { background: rgba(238,247,72,0.72); }
        .scoreDot.warn { background: rgba(255,184,70,0.75); }

        .bar {
          height: 9px; border-radius: 999px;
          background: rgba(255,255,255,.07);
          border: 1px solid rgba(54,209,255,.10); overflow: hidden;
        }
        .barFill { height: 100%; border-radius: 999px; }
        .barFill.good { background: rgba(60,245,128,0.7); }
        .barFill.mid  { background: rgba(238,247,72,0.72); }
        .barFill.warn { background: rgba(255,184,70,0.75); }

        /* COMPARISONS */
        .compareShell { padding: 0; }
        .compareShellInner { padding: 8px; }

        .rowAcc {
          border-radius: 18px; border: 1px solid rgba(54,209,255,.14);
          background: rgba(0,0,0,.08); margin: 10px 6px; overflow: hidden;
        }
        .rowAccHead {
          width: 100%; display: flex; align-items: center;
          justify-content: space-between; gap: 12px; padding: 14px;
          background: transparent; border: none; color: inherit; cursor: pointer;
        }
        .rowAccLeft { display: flex; flex-direction: column; gap: 4px; }
        .rowAccTitle { font-weight: 950; letter-spacing: .01em; font-size: 15px; }
        .rowAccHint { font-size: 13px; color: rgba(234,241,255,.65); }
        .rowAccIcon {
          width: 38px; height: 38px; border-radius: 12px; display: grid; place-items: center;
          border: 1px solid rgba(54,209,255,.18); background: rgba(255,255,255,.05);
          font-size: 18px; line-height: 1;
        }

        .rowAccBody { padding: 0 14px 14px; }

        .compareRow {
          display: grid; grid-template-columns: 190px 1fr;
          gap: 12px; padding: 12px 0;
          border-top: 1px solid rgba(54,209,255,.10);
        }
        @media (max-width: 720px) { .compareRow { grid-template-columns: 1fr; } }
        .compareHeading { font-weight: 950; color: rgba(245,248,255,.92); font-size: 14px; }
        .compareText { color: rgba(234,241,255,.78); line-height: 1.75; font-size: 14px; }
      `}</style>
    </div>
  );
}